// Purpose
//   Lightweight authentication helpers used across the app. This module
//   provides:
//     - isLoggedIn(): quick check whether a valid JWT exists in localStorage
//     - refreshTokenIfNeeded(): refreshes the access token if it's expired or
//       about to expire, otherwise returns the current token.
//
// Important notes
//   - This composable relies on browser localStorage keys:
//       "jwt"            -> current access token (JWT)
//       "refresh_token"  -> refresh token used to obtain a new access token
//       "token_expiry"   -> epoch time (ms) when the access token expires
//   - No state management is used here; storage is the single source of truth.
//   - Network calls are performed with axios against the API base url from
//     ../helper ("url").

import axios from "axios"
import { url } from "../helper"
import { isRefreshing, setRefreshing } from "../composables/auth/refreshState"
import { useLogout } from "../composables/useLogout"
import router from "../router/router"

/**
 * isLoggedIn
 * --------------------------------------------------------------------------
 * Quick client-side check for an active session. A session is considered
 * active if:
 *   1) A JWT exists in localStorage under key "jwt"
 *   2) A numeric expiry exists under key "token_expiry"
 *   3) Current time is before the stored expiry
 *
 * Returns
 *   boolean - true when the above conditions are met, otherwise false.
 *
 * Caveat
 *   This does not validate the token's signature/claims; it only checks local
 *   storage and the stored expiry timestamp.
 */
export function isLoggedIn(): boolean {
	// Read raw values from localStorage
	const token = localStorage.getItem("jwt")
	const expiry = localStorage.getItem("token_expiry")

	//  - token exists
	//  - expiry exists
	return !!token && !!expiry && Date.now() < parseInt(expiry, 10)
}

/**
 * refreshTokenIfNeeded
 * --------------------------------------------------------------------------
 * Returns a valid access token. If the current token is still valid (with a
 * small safety buffer), it returns the existing one. If the token is missing or
 * near/after expiry and a refresh token is available, it attempts to refresh.
 *
 * Storage contract (localStorage keys):
 *   - "jwt": current access token to be returned/updated
 *   - "refresh_token": token used to obtain a new access token
 *   - "token_expiry": epoch time in ms when the access token expires
 *
 * Network behavior:
 *   - POST {url}/oauth/token with grant_type "refresh_token". The API is
 *     expected to return: { access_token, expires_in, refresh_token }
 *
 * Error handling:
 *   - If no refresh token is available when a refresh is needed, an Error is
 *     thrown.
 *   - On refresh failure, all auth-related storage keys are cleared and the
 *     error is re-thrown for the caller to handle (e.g., redirect to login).
 *
 * Returns
 *   Promise<string> - a valid access token (either existing or newly refreshed)
 */
export async function refreshTokenIfNeeded(): Promise<string | null> {
	const { logout } = useLogout(router)
	// Read current tokens and expiry from storage
	const token = localStorage.getItem("jwt")
	const refreshToken = localStorage.getItem("refresh_token")
	const expiry = Number(localStorage.getItem("token_expiry"))

	// Current timestamp in ms
	const now = Date.now()

	// If we have a token and it's not close to expiring (5s buffer), return it
	if (token && expiry && now < expiry - 5) {
		// Token still valid; no refresh required
		return token
	}

	if (!refreshToken) {
		return token ?? null
	}

	// prevent multiple refresh calls
	if (isRefreshing) {
		// Wait until refresh finishes and return the new token
		return new Promise((resolve) => {
			const checker = setInterval(() => {
				// when refreshing ends, return the new jwt
				if (!isRefreshing) {
					clearInterval(checker)
					resolve(localStorage.getItem("jwt"))
				}
			}, 50)
		})
	}

	setRefreshing(true)

	try {
		const response = await axios.post(
			`${url}/oauth/token`,
			{
				grant_type: "refresh_token",
				client_id: "zQTiAtJD02-HEn6fvUTE_I2Y5iKFkA9qJN4vkVDKNbI",
				refresh_token: refreshToken,
			},
			{ headers: { "Content-Type": "application/x-www-form-urlencoded" } }
		)

		const { access_token, expires_in, refresh_token: newRefresh } = response.data

		localStorage.setItem("jwt", access_token)
		localStorage.setItem("refresh_token", newRefresh)
		localStorage.setItem("token_expiry", (Date.now() + expires_in * 1000).toString())

		return access_token
	} catch (error) {
		// user logout when refresh token is invalid
		logout()
		return null
	} finally {
		setRefreshing(false)
	}
}
